export interface IOpioidDrugRouteCombination {
    drugID: number;
    drugNameFr: string;
    drugNameEn: string;
    routeID: number;
    routeNameFr: string;
    routeNameEn: string;
    unitID: number;
    unitName: string;
    specialOpioidFormulaType: number;
}

// SpecialOpioidFormulaType enum values
export enum SpecialOpioidFormulaType {
    None = 0,
    TransdermalFentanyl = 1,
    Methadone = 2,
    Buprenorphine = 3,
}

// Route enum values
export enum DrugRoute {
    Transdermal = 2,
    Oral = 1,
    Sublingual = 3,
    Transdermique = 4,
}

// Fentanyl dose equivalent for dropdown options
export interface IFentanylDoseEquivalent {
    doseMcgPerHour: number;
    mmeValue: number;
}

// TransdermalFentanyl configuration
export interface ITransdermalFentanylConfig {
    doseEquivalents: IFentanylDoseEquivalent[];
    fallbackFactor: number;
}

export interface IOpioidDoseInput {
    drugID: number;
    routeID: number;
    dose: number;
    unitID: number;
    frequencyPerDay: number;
}

export interface IOpioidDoseCalculation {
    drugID: number;
    drugName: string;
    routeID: number;
    routeName: string;
    dose: number;
    unitID: number;
    frequencyPerDay: number;
    conversionFactor: number;
    dailyMME: number;
    hasWarning: boolean;
    warningMessage: string | null;
}

export interface IMMECalculationRequest {
    opioidDoses: IOpioidDoseInput[];
}

export enum MMERiskLevel {
    None = 0,
    Moderate = 1,
    High = 2,
}

export interface IMMECalculationResult {
    totalDailyMME: number;
    standardUsed: string | null;
    calculations: IOpioidDoseCalculation[];
    warnings: string[];
    riskLevel: MMERiskLevel;
    riskLevelDisplay: string;
    riskMessage: string | null;
    isBlocking: boolean;
}

export interface ISaveMMECalculationRequest {
    patientID: number;
    savedCalculationId?: number;
    calculationRequest: IMMECalculationRequest;
    calculationResult: IMMECalculationResult;
}

export interface IRiskThreshold {
    moderateThreshold: number;
    highThreshold: number;
    isBlocking: boolean;
}

export interface IOpioidConversionVersion {
    id: number;
    versionName: string;
    description: string;
    isActive: boolean;
    createdDate: string;
    riskThresholds?: IRiskThreshold;
}

export interface ISavedMMECalculation {
    id: number;
    patientID: number;
    healthcareProfessionalID: number;
    healthcareProfessionalName: string;
    totalDailyMME: number;
    riskLevel: string; // 'Low' | 'Moderate' | 'High'
    riskMessage: string | null; // Generated risk message with threshold values
    conversionDataVersionID: number;
    conversionDataVersion: IOpioidConversionVersion;
    referenceVersion: string; // Legacy field - use conversionDataVersion.versionName instead
    calculationDate: string;
    createdDate: string;
    canBeDeleted: boolean;
    inputData: IOpioidDoseInput[];
    calculationDetails: IOpioidDoseCalculation[];
    warnings: string[];
}

// Drug dosage - available dosage strengths per drug
export interface IDrugDosage {
    id: number;
    drugID: number;
    drugName: string;
    routeID: number;
    routeName: string;
    dosageStrength: number;
    unitID: number;
    unitName: string;
    formType: string;
    formDescription: string;
    isActive: boolean;
    sortOrder: number;
}

export interface ICreateDrugDosageRequest {
    drugID: number;
    routeID: number;
    dosageStrength: number;
    unitID: number;
    formType: string;
    formDescriptionEn: string;
    formDescriptionFr: string;
    sortOrder: number;
}

export interface IUpdateDrugDosageRequest {
    dosageStrength: number;
    unitID: number;
    formType: string;
    formDescriptionEn: string;
    formDescriptionFr: string;
    isActive: boolean;
    sortOrder: number;
}

// Dose suggestion result from backend
export interface IDoseSuggestionResult {
    targetMME: number;
    hasSuggestion: boolean;
    dosageID: number;
    dosageStrength: number;
    formType: string;
    frequencyPerDay: number;
    conversionFactor: number;
    actualMME: number;
}

export interface IBatchDoseSuggestionRequest {
    targetMMEs: number[];
    drugID: number;
    routeID: number;
}

